/**
 * The active practice session in this tab (AIC-463). It lives in sessionStorage (so a reload stays in practice and a new
 * tab does not), every access wrapped in try/catch: with storage unavailable it is held in memory. The notice and the
 * return route are in memory only. Dependency-free, so the API client and sign-out can use it without loading a screen.
 *
 * Ending practice (the user's Exit, a reset, or a 410 from the practice hook) clears the session and sets `returnTo`,
 * the source assignment's page; the app navigates there once and takes it (takePracticeReturn).
 */
import { useSyncExternalStore } from "react";

/** The sessionStorage key. It starts with PRACTICE_STORAGE_PREFIX, which clearPortalStorage removes on sign-out. */
export const PRACTICE_STORAGE_PREFIX = "swtracker.practice.";
export const PRACTICE_STORAGE_KEY = `${PRACTICE_STORAGE_PREFIX}session`;

export interface PracticeSession {
  readonly id: string;
  readonly company: string;
  /** The real assignment the session was copied from: where Exit goes back to. */
  readonly sourceAssignmentId: string;
  /** The practice copy's assignment id. */
  readonly assignmentId: string;
}

export interface PracticeState {
  readonly session: PracticeSession | null;
  /** Shown once after practice ended on its own (a reset, an ended session). */
  readonly notice: string | null;
  readonly returnTo: string | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const listeners = new Set<() => void>();
let memoryRaw: string | null = null;
let notice: string | null = null;
let returnTo: string | null = null;
let entered = false;
let previewActive = false;
let snap: PracticeState = { session: null, notice: null, returnTo: null };
let snapRaw: string | null | undefined;

function readRaw(): string | null {
  try { return window.sessionStorage.getItem(PRACTICE_STORAGE_KEY); } catch { return memoryRaw; }
}
function writeRaw(raw: string | null): void {
  memoryRaw = raw;
  try {
    if (raw === null) window.sessionStorage.removeItem(PRACTICE_STORAGE_KEY);
    else window.sessionStorage.setItem(PRACTICE_STORAGE_KEY, raw);
  } catch { /* storage unavailable: memory holds it */ }
}
function parse(raw: string | null): PracticeSession | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<Record<keyof PracticeSession, unknown>>;
    if (typeof v.id !== "string" || !UUID.test(v.id) || typeof v.company !== "string" || typeof v.sourceAssignmentId !== "string" || typeof v.assignmentId !== "string") return null;
    return { id: v.id, company: v.company, sourceAssignmentId: v.sourceAssignmentId, assignmentId: v.assignmentId };
  } catch { return null; }
}
const emit = () => { for (const fn of listeners) fn(); };

/** The current state; the same object until something changes (a useSyncExternalStore snapshot). */
export function getPracticeState(): PracticeState {
  const raw = readRaw();
  if (raw !== snapRaw || snap.notice !== notice || snap.returnTo !== returnTo) {
    snapRaw = raw;
    snap = { session: parse(raw), notice, returnTo };
  }
  return snap;
}
/** The active session's id, or null. */
export const activePracticeId = (): string | null => (previewActive ? null : getPracticeState().session?.id ?? null);

/** An analyst preview is active: no request carries the practice header, whatever the browser holds (AIC-463). */
export function setPracticePreview(on: boolean): void { previewActive = on; }

/** Sign-out: forgets the in-memory fallback used when sessionStorage is blocked (the storage key itself is cleared by prefix). */
export function clearPracticeMemory(): void {
  if (memoryRaw === null) return;
  memoryRaw = null;
  emit();
}

export function startPractice(session: PracticeSession): void {
  notice = null; returnTo = null; entered = true;
  writeRaw(JSON.stringify(session));
  emit();
}

/** Leaves practice: clears the session and sends the app to the source assignment (unless `stay`), with an optional notice. */
export function endPractice(why: { notice?: string; stay?: boolean } = {}): void {
  const was = getPracticeState().session;
  writeRaw(null);
  notice = why.notice ?? null;
  returnTo = was && !why.stay ? `/assignments/${encodeURIComponent(was.sourceAssignmentId)}` : null;
  emit();
}
export function takePracticeReturn(): string | null {
  const to = returnTo;
  if (to === null) return null;
  returnTo = null;
  emit();
  return to;
}
/** True once after startPractice: the banner then takes focus (a reload that finds a session already active does not). */
export function takePracticeEntered(): boolean {
  const was = entered;
  entered = false;
  return was;
}
export function dismissPracticeNotice(): void {
  if (notice === null) return;
  notice = null;
  emit();
}
export function subscribePractice(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
export const usePracticeState = (): PracticeState => useSyncExternalStore(subscribePractice, getPracticeState, getPracticeState);

export function resetPracticeStoreForTests(): void {
  memoryRaw = null; notice = null; returnTo = null; entered = false; previewActive = false; snapRaw = undefined; snap = { session: null, notice: null, returnTo: null };
  listeners.clear();
}
